import { PLAN_VERSIONS } from '../../utils/constants';

/**
 * Client-side model for the SPMS GetRiskEvaluation API. Field names mirror
 * `GetRiskEvaluationResponse` defined in ShiftPlanManagementServiceModel
 * (CR-275327551). The `status` value space isn't strictly typed on the
 * server side yet — these are the working set we expect.
 *
 * @typedef {Object} RiskEvaluation
 * @property {string} planId
 * @property {keyof typeof RISK_STATUS} status
 * @property {string} [triggerSource]
 * @property {number} [evaluatedAt]
 * @property {string} [nodeId]
 * @property {number} [totalBreaches]
 * @property {RiskResult[]} [results]
 * @property {string} [errorMessage]
 *
 * @typedef {Object} RiskResult
 * @property {string} guardrailId
 * @property {string} displayName
 * @property {string} [startTime]
 * @property {string} [endTime]
 * @property {string} [cycleName]
 * @property {boolean} breached
 * @property {string} [explanation]
 */

export const RISK_STATUS = {
  IN_PROGRESS: 'IN_PROGRESS', // engine is still evaluating
  COMPLETED: 'COMPLETED', // evaluation done; check totalBreaches / results
  FAILED: 'FAILED' // engine errored; errorMessage may be populated
};

/** Plan versions that have Risk Engine evaluation enabled. */
export const RISK_ENGINE_PLAN_VERSIONS = new Set([PLAN_VERSIONS.STATIC_V3, PLAN_VERSIONS.DYNAMIC_V2]);

/** True while we're still waiting for a final answer (no response yet OR engine in progress). */
export const isRiskScanning = (re) => !re || re.status === RISK_STATUS.IN_PROGRESS;

/** True when the engine has finished and reported one or more active risks. */
export const hasActiveRisks = (re) =>
  re?.status === RISK_STATUS.COMPLETED && (re.totalBreaches ?? re.results?.filter((r) => r.breached).length ?? 0) > 0;

/** Approve is always enabled — risks are advisory, never blocking. */
export const canApprovePlan = () => true;

/** Number of active risks reported, or null if not yet known. */
export const getActiveRiskCount = (re) => {
  if (!re || re.status !== RISK_STATUS.COMPLETED) return null;
  return re.totalBreaches ?? re.results?.filter((r) => r.breached).length ?? 0;
};
